import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Copy, Share2, Check } from "lucide-react";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import api from "@/lib/api";
import { trackTrailer } from "@/lib/trailerTracker";

function TrailerShare({ url }) {
    const [copied, setCopied] = useState(false);
    const pageUrl = typeof window !== "undefined" ? `${window.location.origin}/original-series#trailer` : url;
    const copyLink = async () => {
        try {
            await navigator.clipboard.writeText(pageUrl);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
            toast.success("Trailer link copied");
        } catch { toast.error("Couldn't copy — long press to copy manually"); }
    };
    const share = async () => {
        try {
            if (navigator.share) {
                await navigator.share({ title: "Mrs.Black(In)Love — Our Original Series", text: "First look at a series that shows the way we actually love. Cast finalized — filming begins October.", url: pageUrl });
            } else {
                await copyLink();
            }
        } catch (err) {
            // Native share was cancelled or copy failed — safe to ignore, no user impact.
            void err;
        }
    };
    return (
        <div className="flex gap-3 shrink-0" data-testid="trailer-share">
            <button onClick={copyLink} className="btn-gold text-[0.65rem]" data-testid="trailer-copy">
                {copied ? <Check size={12} /> : <Copy size={12} />} {copied ? "Copied" : "Copy link"}
            </button>
            <button onClick={share} className="btn-gold btn-gold-solid text-[0.65rem]" data-testid="trailer-share-btn">
                <Share2 size={12} /> Share
            </button>
        </div>
    );
}

const HERO =
    "https://images.unsplash.com/photo-1758738180478-0fd260c86605?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjV8MHwxfHNlYXJjaHwzfHxibGFjayUyMHdvbWFuJTIwcG9ydHJhaXQlMjBlbGVnYW50JTIwZ29sZHxlbnwwfHx8fDE3ODU2ODY4ODl8MA&ixlib=rb-4.1.0&q=85";

const TRAILER_URL = "https://customer-assets-lxgj4vgw.emergentagent.net/job_elevated-collective/artifacts/8hj3ckuz_copy_5EC9987C-0287-4D7B-AB31-1FC6B02F703E.mov";

// Chapter One casting closed. When the user uploads the five balloon-post PNGs
// they mocked up (Cast Finalized · Filming October · Pre-filming Mixer · 35+
// callbacks · Applications Closed), swap the empty `src` in each entry. Until
// then we render an on-brand typographic card so the strip still ships.
const BALLOON_POSTS = [
    { src: "", tag: "Cast Finalized", headline: "The cast is finalized.", sub: "The house is chosen.", accent: "Filming begins in October" },
    { src: "", tag: "October", headline: "Filming begins in October.", sub: "One house. One weekend.", accent: "Chapter One" },
    { src: "", tag: "Pre-Filming Mixer", headline: "Pre-filming mixer.", sub: "Happening with the final cast in Atlanta within the next three weeks.", accent: "By invitation" },
    { src: "", tag: "Callbacks", headline: "Over 35 women made it to the 2nd round of callbacks.", sub: "Personally reviewed by the casting team.", accent: "Round two" },
    { src: "", tag: "Applications Closed", headline: "Over 200 women applied.", sub: "Applications are now closed.", accent: "Thank you" },
];

const FAQ = [
    { q: "Is casting still open?", a: "No. Casting for Chapter One is closed. Fourteen women have been selected, the house is secured, and we begin filming this October. Future casting calls will be announced through Letters from the Table." },
    { q: "When does the series release?", a: "A release window will be announced once post-production is scoped. Subscribe below and you'll be the first to know when a date is set." },
    { q: "Will there be a Chapter Two?", a: "Yes — additional chapters and cities are already in planning. Follow along and we'll open casting for the next chapter when the time is right." },
    { q: "How do I get behind-the-scenes updates?", a: "Watch the trailer, follow our socials, and subscribe to Letters from the Table for occasional dispatches from the set." },
];

const CAST_STATS = [
    { n: "200+", t: "Women applied", d: "From cities across the country." },
    { n: "35+", t: "Second-round callbacks", d: "Personally reviewed by the casting team." },
    { n: "14", t: "Final housemates selected", d: "Married, dating, and single — the range of lesbian love." },
    { n: "Oct.", t: "Filming begins", d: "Inside the private residence we secured for Chapter One." },
];

export default function OriginalSeries() {
    const [seasons, setSeasons] = useState([]);
    useEffect(() => { api.get("/seasons").then((r) => setSeasons(r.data)).catch(() => {}); }, []);
    return (
        <main data-testid="series-page" className="pt-0">
            <section className="relative min-h-[100vh] w-full grain overflow-hidden">
                <video
                    src={TRAILER_URL}
                    poster={HERO}
                    autoPlay
                    muted
                    loop
                    playsInline
                    preload="auto"
                    onPlay={() => trackTrailer('series-hero-autoplay', 'play')}
                    className="absolute inset-0 h-full w-full object-cover"
                    data-testid="series-hero-video"
                />
                <div className="absolute inset-0 hero-mask" />
                <div className="relative z-10 mx-auto max-w-[1600px] px-6 lg:px-12 pt-40 pb-24 min-h-[100vh] flex flex-col justify-end">
                    <p className="eyebrow" data-testid="series-hero-eyebrow">Chapter One · Cast Finalized · Filming October</p>
                    <h1 className="font-display text-6xl md:text-9xl mt-6 max-w-5xl leading-[0.92]">
                        A series that shows the way we <em className="text-[color:var(--gold)]">actually love</em>.
                    </h1>
                    <p className="mt-8 max-w-2xl text-[color:var(--cream-muted)] text-lg">
                        An unscripted, cinematic original series centered on everyday lesbian women — married, dating, or single. Fourteen women selected. One house, secured. Filming begins in October. Stay tuned.
                    </p>
                    <div className="mt-10 flex flex-wrap gap-4">
                        <a href="#trailer" onClick={() => trackTrailer('series-hero-cta', 'click')} className="btn-gold btn-gold-solid" data-testid="series-watch-trailer">▶ Watch the trailer</a>
                        <a href="#cast-finalized" className="btn-gold" data-testid="series-hero-cast-anchor">See the milestones</a>
                    </div>
                </div>
            </section>

            <section id="trailer" className="py-24 border-t border-[color:var(--espresso)] bg-[color:var(--warm-black)] scroll-mt-24" data-testid="trailer-section">
                <div className="mx-auto max-w-[1600px] px-6 lg:px-12">
                    <div className="flex items-baseline gap-6 mb-10">
                        <p className="eyebrow">The Trailer</p>
                        <div className="hairline" />
                        <span className="text-[0.65rem] tracking-[0.28em] uppercase text-[color:var(--cream-muted)]">First look</span>
                    </div>
                    <div className="relative border border-[color:var(--gold)]/30 overflow-hidden bg-black" data-testid="trailer-player">
                        <video
                            src={TRAILER_URL}
                            controls
                            playsInline
                            preload="metadata"
                            poster={HERO}
                            onPlay={() => trackTrailer('series-page-player', 'play')}
                            className="w-full h-auto max-h-[80vh] object-contain bg-black"
                        >
                            Your browser doesn't support inline video. <a href={TRAILER_URL} className="text-[color:var(--gold)] underline">Download the trailer</a>.
                        </video>
                    </div>
                    <div className="mt-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
                        <p className="text-[color:var(--cream-muted)] max-w-2xl text-sm">
                            An early look at what we're building. Casting for Chapter One is now closed — filming begins in October.
                        </p>
                        <TrailerShare url={TRAILER_URL} />
                    </div>
                </div>
            </section>

            {/* CAST FINALIZED — replaces the old "Casting is open" footer CTA */}
            <section id="cast-finalized" className="py-32 border-t border-[color:var(--espresso)] bg-[color:var(--warm-black)] scroll-mt-24 relative overflow-hidden" data-testid="cast-finalized">
                <div aria-hidden className="absolute inset-0 opacity-[0.05] pointer-events-none" style={{ backgroundImage: "radial-gradient(circle at 20% 20%, var(--gold), transparent 40%), radial-gradient(circle at 80% 70%, var(--wine), transparent 45%)" }} />
                <div className="relative mx-auto max-w-[1600px] px-6 lg:px-12">
                    <div className="flex items-baseline gap-6">
                        <p className="eyebrow">The Cast is Finalized</p>
                        <div className="hairline" />
                    </div>
                    <h2 className="font-display text-5xl md:text-8xl mt-6 max-w-4xl leading-[0.95]">
                        Fourteen women. One house.<br />
                        <em className="text-[color:var(--gold)]">Filming begins in October.</em>
                    </h2>
                    <p className="mt-8 max-w-2xl text-[color:var(--cream-muted)] text-lg">
                        Casting for Chapter One is officially closed. The house is chosen, the schedule is set, and a pre-filming mixer with the final cast is being planned in Atlanta within the next three weeks.
                    </p>
                    <div className="mt-16 grid sm:grid-cols-2 lg:grid-cols-4 gap-6" data-testid="cast-finalized-stats">
                        {CAST_STATS.map((s, i) => (
                            <div key={s.t} className="border border-[color:var(--espresso)] p-6 bg-[color:var(--dark-grey)]/50" data-testid={`cast-stat-${i}`}>
                                <span className="font-display text-5xl md:text-6xl text-[color:var(--gold)] leading-none">{s.n}</span>
                                <p className="mt-4 font-serif text-xl text-[color:var(--soft-cream)]">{s.t}</p>
                                <p className="mt-2 text-sm text-[color:var(--cream-muted)] leading-relaxed">{s.d}</p>
                            </div>
                        ))}
                    </div>

                    {/* Balloon-post scroll strip. Renders uploaded PNGs when
                        `src` is set (see BALLOON_POSTS); otherwise falls back to
                        an on-brand typographic card that matches the design. */}
                    <div className="mt-20">
                        <div className="flex items-baseline gap-6 mb-8">
                            <p className="eyebrow" data-testid="balloon-strip-eyebrow">The announcement posts</p>
                            <div className="hairline" />
                            <span className="text-[0.65rem] tracking-[0.28em] uppercase text-[color:var(--cream-muted)] hidden md:inline">Scroll →</span>
                        </div>
                        <div
                            className="flex gap-6 overflow-x-auto pb-6 snap-x snap-mandatory scroll-smooth -mx-6 lg:-mx-12 px-6 lg:px-12"
                            style={{ scrollbarWidth: "thin" }}
                            data-testid="balloon-strip"
                        >
                            {BALLOON_POSTS.map((p, i) => (
                                <article
                                    key={p.tag}
                                    className="relative shrink-0 snap-start w-[280px] md:w-[320px] aspect-[4/5] overflow-hidden border border-[color:var(--gold)]/30 bg-gradient-to-br from-[color:var(--wine)] via-[#3a1420] to-[color:var(--warm-black)]"
                                    data-testid={`balloon-post-${i}`}
                                >
                                    {p.src ? (
                                        <img src={p.src} alt={`${p.tag} announcement`} className="absolute inset-0 h-full w-full object-cover" />
                                    ) : (
                                        <>
                                            {/* CSS "balloon" — placeholder until real PNGs are uploaded */}
                                            <div
                                                aria-hidden
                                                className="absolute -left-8 -bottom-4 w-56 h-64 rounded-full opacity-70"
                                                style={{
                                                    background: "radial-gradient(circle at 30% 30%, #f5c8b0 0%, #d89a7f 40%, #a8674f 75%, transparent 78%)",
                                                }}
                                            />
                                            <span
                                                aria-hidden
                                                className="absolute left-14 bottom-0 w-px h-14"
                                                style={{ background: "linear-gradient(to bottom, #a8674f, transparent)" }}
                                            />
                                            <div className="relative z-10 h-full flex flex-col justify-between p-6">
                                                <p className="text-[0.6rem] uppercase tracking-[0.28em] text-[color:var(--gold)]">{p.tag}</p>
                                                <div>
                                                    <h3 className="font-display text-2xl md:text-3xl leading-[1.05] text-[color:var(--soft-cream)]">
                                                        {p.headline}
                                                    </h3>
                                                    {p.sub && (
                                                        <p className="mt-3 font-serif text-base text-[color:var(--cream-muted)] leading-snug">{p.sub}</p>
                                                    )}
                                                    <p className="mt-4 text-[0.68rem] uppercase tracking-[0.24em] text-[color:var(--gold)]">{p.accent}</p>
                                                    <p className="mt-3 text-[0.6rem] uppercase tracking-[0.24em] text-[color:var(--cream-muted)]/70">MrsBlackInLove.org</p>
                                                </div>
                                            </div>
                                        </>
                                    )}
                                </article>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            <section className="py-32 border-t border-[color:var(--espresso)]">
                <div className="mx-auto max-w-[1600px] px-6 lg:px-12 grid lg:grid-cols-12 gap-16">
                    <div className="lg:col-span-4">
                        <p className="eyebrow">Mission</p>
                    </div>
                    <div className="lg:col-span-8">
                        <p className="font-serif text-3xl md:text-5xl leading-tight">
                            To document the range of lesbian love — the long marriages, the tender dating seasons, the powerful single years — without flattening any of it into a headline.
                        </p>
                    </div>
                </div>
            </section>

            <section className="py-32 border-t border-[color:var(--espresso)] bg-[color:var(--dark-grey)]">
                <div className="mx-auto max-w-[1600px] px-6 lg:px-12 grid lg:grid-cols-12 gap-16">
                    <div className="lg:col-span-5">
                        <p className="eyebrow">The Weekend</p>
                        <h2 className="font-display text-5xl md:text-7xl mt-4">Friday through Sunday.</h2>
                    </div>
                    <div className="lg:col-span-7 text-[color:var(--cream-muted)]">
                        <p className="text-lg leading-relaxed">
                            Filming takes place over a single weekend — Friday through Sunday — inside a private residence. Selected housemates will receive full arrival details, a private schedule, and everything they need to know once cast.
                        </p>
                    </div>
                </div>
            </section>

            {/* PARTNER + CREW APPLY — routes to the existing /partnerships form */}
            <section id="partner-apply" className="py-32 border-t border-[color:var(--espresso)] relative overflow-hidden" data-testid="partner-apply">
                <div aria-hidden className="absolute inset-0 opacity-[0.05] pointer-events-none" style={{ backgroundImage: "radial-gradient(circle at 80% 20%, var(--gold), transparent 45%), radial-gradient(circle at 15% 85%, var(--wine), transparent 40%)" }} />
                <div className="relative mx-auto max-w-[1600px] px-6 lg:px-12 grid lg:grid-cols-12 gap-16">
                    <div className="lg:col-span-5">
                        <p className="eyebrow">Partner with the series</p>
                        <h2 className="font-display text-5xl md:text-7xl mt-4 leading-[0.95]">
                            Build the house <em className="text-[color:var(--gold)]">with us</em>.
                        </h2>
                        <p className="mt-8 text-[color:var(--cream-muted)] text-lg leading-relaxed">
                            We're actively partnering with queer-, Black-, minority-, and women-owned businesses and creators to shape the world inside Chapter One — on camera, on the table, and behind the scenes.
                        </p>
                        <div className="mt-10 flex flex-wrap gap-4">
                            <Link to="/partnerships" className="btn-gold btn-gold-solid" data-testid="partner-apply-cta">
                                Apply to partner
                            </Link>
                            <Link to="/partnerships#brands" className="btn-gold" data-testid="partner-apply-tracks">
                                See partnership tracks
                            </Link>
                        </div>
                    </div>
                    <div className="lg:col-span-7">
                        <p className="eyebrow" data-testid="partner-apply-list-label">Currently soliciting — tap to apply</p>
                        <ul className="mt-6 grid sm:grid-cols-2 gap-x-8 gap-y-3 font-serif text-lg text-[color:var(--soft-cream)]" data-testid="partner-apply-list">
                            {[
                                { label: "Service providers", type: "custom_activation" },
                                { label: "Chefs & caterers", type: "custom_activation" },
                                { label: "Beverage & food brands", type: "product_placement" },
                                { label: "Wine, beer & spirits", type: "product_placement" },
                                { label: "Social games & activations", type: "custom_activation" },
                                { label: "Retail brands & product placement", type: "product_placement" },
                                { label: "Aspiring content creators", type: "social_content" },
                                { label: "Crew members (BTS gear provided)", type: "bts_crew" },
                            ].map((item, i) => (
                                <li key={item.label} className="border-b border-[color:var(--espresso)]">
                                    <Link
                                        to={`/partnerships?type=${item.type}&interest=${encodeURIComponent(item.label)}#partner-inquiry-form`}
                                        className="flex items-baseline gap-3 py-3 hover:text-[color:var(--gold)] transition-colors duration-300 group"
                                        data-testid={`partner-apply-item-${i}`}
                                    >
                                        <span className="text-[color:var(--gold)] text-sm">—</span>
                                        <span className="flex-1">{item.label}</span>
                                        <span className="text-[0.6rem] uppercase tracking-[0.24em] text-[color:var(--cream-muted)] group-hover:text-[color:var(--gold)]">Apply →</span>
                                    </Link>
                                </li>
                            ))}
                        </ul>
                        <p className="mt-6 text-sm text-[color:var(--cream-muted)] leading-relaxed max-w-xl">
                            Priority given to queer-owned, minority- and Black-owned, and women-owned businesses. Aspiring content creators and crew are welcome to apply — we provide the gear, you bring the vision.
                        </p>
                    </div>
                </div>
            </section>

            <section className="py-32 border-t border-[color:var(--espresso)]">
                <div className="mx-auto max-w-[1600px] px-6 lg:px-12 grid lg:grid-cols-12 gap-16">
                    <div className="lg:col-span-4">
                        <p className="eyebrow">Frequently asked</p>
                        <h2 className="font-display text-4xl md:text-6xl mt-4">Answers.</h2>
                    </div>
                    <div className="lg:col-span-8">
                        <Accordion type="single" collapsible data-testid="faq-accordion" className="w-full">
                            {FAQ.map((f) => (
                                <AccordionItem key={f.q} value={`item-${f.q}`} className="border-b border-[color:var(--espresso)]">
                                    <AccordionTrigger className="text-left font-serif text-2xl py-6 hover:text-[color:var(--gold)]">{f.q}</AccordionTrigger>
                                    <AccordionContent className="text-[color:var(--cream-muted)] pb-6">{f.a}</AccordionContent>
                                </AccordionItem>
                            ))}
                        </Accordion>
                    </div>
                </div>
            </section>

            <section className="py-32 border-t border-[color:var(--espresso)] bg-[color:var(--dark-grey)] relative overflow-hidden" data-testid="season-archive">
                <div className="absolute inset-0 opacity-[0.04] pointer-events-none" style={{ backgroundImage: "radial-gradient(circle at 20% 20%, var(--gold), transparent 40%), radial-gradient(circle at 80% 70%, var(--wine), transparent 45%)" }} />
                <div className="relative mx-auto max-w-[1600px] px-6 lg:px-12">
                    <div className="flex items-baseline gap-6">
                        <p className="eyebrow">The Archive</p>
                        <span className="text-[0.65rem] tracking-[0.28em] uppercase text-[color:var(--gold)] border border-[color:var(--gold)]/50 px-3 py-1">Coming soon</span>
                    </div>
                    <h2 className="font-display text-5xl md:text-8xl mt-6 max-w-4xl leading-[0.95]">The record<br /><em className="text-[color:var(--gold)]">begins here</em>.</h2>
                    <div className="mt-14 grid lg:grid-cols-12 gap-16">
                        <div className="lg:col-span-6">
                            <p className="font-serif text-2xl md:text-3xl leading-relaxed text-[color:var(--soft-cream)]">
                                Once the debut cast films this October and the first cut releases, every chapter — with its cast portraits, synopsis, and behind-the-scenes moments — will live in this space.
                            </p>
                            <p className="mt-6 text-[color:var(--cream-muted)] max-w-lg">
                                What you'll see here is being built from scratch, alongside our first cast, in real time. Join the priority list and you'll be first to watch when it drops.
                            </p>
                            <div className="mt-10 flex flex-wrap gap-4">
                                <a href="#trailer" className="btn-gold btn-gold-solid" data-testid="archive-watch-trailer-cta">▶ Watch the trailer</a>
                                <a href="/#letters-from-the-table" className="btn-gold" data-testid="archive-notify">Notify me when it releases</a>
                            </div>
                        </div>
                        <div className="lg:col-span-6 relative">
                            <div className="border border-[color:var(--gold)]/30 aspect-[4/5] flex flex-col items-center justify-center p-10 relative overflow-hidden" data-testid="archive-placeholder">
                                <div className="absolute inset-4 border border-[color:var(--gold)]/15" />
                                <span className="font-serif italic text-[color:var(--gold)] text-lg">Ch. 01</span>
                                <p className="mt-4 font-display text-4xl md:text-5xl text-center text-[color:var(--cream-muted)]">Cast finalized</p>
                                <div className="mt-6 flex flex-col items-center gap-2 text-[0.65rem] uppercase tracking-[0.28em] text-[color:var(--cream-muted)]">
                                    <span>Cast · 14 selected</span>
                                    <span>Filming · October</span>
                                    <span>Release · To be announced</span>
                                </div>
                                <div className="mt-10 flex gap-2">
                                    {[1, 2, 3, 4].map((n) => (
                                        <span key={n} className="w-2 h-2 border border-[color:var(--gold)]/40" />
                                    ))}
                                </div>
                                <a href="#trailer" className="mt-8 link-underline text-xs uppercase tracking-[0.24em] text-[color:var(--gold)]" data-testid="archive-watch-trailer">▶ Watch the trailer</a>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <section className="py-32 border-t border-[color:var(--espresso)] bg-[color:var(--wine)]/20">
                <div className="mx-auto max-w-[1600px] px-6 lg:px-12 text-center">
                    <p className="eyebrow" data-testid="stay-tuned-eyebrow">Stay tuned</p>
                    <h2 className="font-display text-6xl md:text-9xl mt-6">The story starts <em className="text-[color:var(--gold)]">in October</em>.</h2>
                    <p className="mt-8 max-w-xl mx-auto text-[color:var(--cream-muted)]">Casting is closed. Fourteen women, one house, one weekend. Subscribe to Letters from the Table and you'll be the first to hear when the first cut releases.</p>
                    <div className="mt-10 flex flex-wrap items-center justify-center gap-4">
                        <a href="/#letters-from-the-table" className="btn-gold btn-gold-solid" data-testid="stay-tuned-subscribe">Get release updates</a>
                        <a href="#trailer" className="btn-gold" data-testid="stay-tuned-trailer">▶ Watch the trailer</a>
                    </div>
                </div>
            </section>

            {/* CHAPTER TWO WAITLIST — lightweight interest form for the next chapter */}
            <ChapterTwoWaitlist />
        </main>
    );
}

function ChapterTwoWaitlist() {
    const [form, setForm] = useState({ first_name: "", email: "", city: "", notes: "" });
    const [busy, setBusy] = useState(false);
    const [done, setDone] = useState(false);
    const set = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));
    const submit = async (e) => {
        e.preventDefault();
        if (!form.email || !/^\S+@\S+\.\S+$/.test(form.email)) {
            toast.error("Please enter a valid email so we can reach you.");
            return;
        }
        setBusy(true);
        try {
            await api.post("/casting/waitlist", form);
            setDone(true);
            toast.success("You're on the list — we'll reach out when Chapter Two casting opens.");
        } catch (err) {
            const detail = err?.response?.data?.detail;
            toast.error(typeof detail === "string" ? detail : "Couldn't save that — please try again.");
        } finally {
            setBusy(false);
        }
    };

    if (done) {
        return (
            <section className="py-24 border-t border-[color:var(--espresso)] bg-[color:var(--warm-black)]" data-testid="waitlist-thanks">
                <div className="mx-auto max-w-2xl px-6 text-center">
                    <p className="eyebrow">You're on the list</p>
                    <h2 className="font-display text-4xl md:text-6xl mt-6">Thank you.</h2>
                    <p className="mt-6 text-[color:var(--cream-muted)]">When Chapter Two casting opens, you'll be among the first to hear. Please add casting@mrsblackinlove.org to your contacts so nothing lands in spam.</p>
                </div>
            </section>
        );
    }

    return (
        <section id="chapter-two-waitlist" className="py-24 border-t border-[color:var(--espresso)] bg-[color:var(--warm-black)] scroll-mt-24" data-testid="chapter-two-waitlist">
            <div className="mx-auto max-w-[1200px] px-6 lg:px-12 grid lg:grid-cols-12 gap-16">
                <div className="lg:col-span-5">
                    <p className="eyebrow">Chapter Two — Interest list</p>
                    <h2 className="font-display text-4xl md:text-6xl mt-4 leading-[0.95]">
                        Missed Chapter One? <em className="text-[color:var(--gold)]">Get on the list.</em>
                    </h2>
                    <p className="mt-6 text-[color:var(--cream-muted)] leading-relaxed">
                        Additional chapters and cities are already in planning. Drop your info and you'll be the first to know when the next casting call opens.
                    </p>
                </div>
                <form onSubmit={submit} className="lg:col-span-7 border border-[color:var(--espresso)] p-8 space-y-5" data-testid="waitlist-form">
                    <div className="grid sm:grid-cols-2 gap-5">
                        <input
                            className="input-line"
                            placeholder="First name"
                            value={form.first_name}
                            onChange={set("first_name")}
                            data-testid="waitlist-first-name"
                        />
                        <input
                            className="input-line"
                            type="email"
                            required
                            placeholder="Email"
                            value={form.email}
                            onChange={set("email")}
                            data-testid="waitlist-email"
                        />
                    </div>
                    <input
                        className="input-line"
                        placeholder="City (optional)"
                        value={form.city}
                        onChange={set("city")}
                        data-testid="waitlist-city"
                    />
                    <textarea
                        className="input-line min-h-[100px]"
                        placeholder="Anything you'd want us to know? (optional)"
                        value={form.notes}
                        onChange={set("notes")}
                        data-testid="waitlist-notes"
                    />
                    <div className="flex flex-wrap items-center justify-between gap-4 pt-2">
                        <p className="text-xs text-[color:var(--cream-muted)] max-w-xs">Free to join. We only email when Chapter Two casting is live.</p>
                        <button
                            type="submit"
                            disabled={busy}
                            className="btn-gold btn-gold-solid disabled:opacity-40"
                            data-testid="waitlist-submit"
                        >
                            {busy ? "Adding you…" : "Join the interest list"}
                        </button>
                    </div>
                </form>
            </div>
        </section>
    );
}
